/**
 * /sudo/inbox — list of conversations.
 *
 * Filter pills along the top + a table of every conversation
 * sorted by most-recently-updated. Click a row → /sudo/inbox/$id.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ArrowRight, Mail, MessageSquare, Megaphone } from 'lucide-react';
import { useDocumentTitle } from '../lib/use-document-title';

type Status = 'new' | 'reading' | 'replied' | 'closed' | 'spam';
type Channel = 'contact_form' | 'app_contact_form' | 'direct_email';

interface ConversationRow {
  id: string;
  status: Status;
  channel: Channel;
  name: string | null;
  email: string | null;
  painSummary: string | null;
  userId: string | null;
  tenantId: string | null;
  createdAt: number;
  updatedAt: number;
}

const FILTERS: { value: Status | 'all' | 'unread'; label: string }[] = [
  { value: 'unread', label: 'Unread' },
  { value: 'all', label: 'All' },
  { value: 'replied', label: 'Replied' },
  { value: 'closed', label: 'Closed' },
  { value: 'spam', label: 'Spam' },
];

async function fetchConversations(filter: string): Promise<{ conversations: ConversationRow[] }> {
  const url = new URL('/api/super/conversations', window.location.origin);
  if (filter !== 'all' && filter !== 'unread') {
    url.searchParams.set('status', filter);
  }
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error('list_failed');
  const raw = (await res.json()) as { conversations?: ConversationRow[] };
  // Guard against an unexpected shape (e.g. a cached value from a
  // different query that shares this key by mistake). Default to an
  // empty list rather than throwing on `.length` deep in render.
  const conversations = Array.isArray(raw.conversations) ? raw.conversations : [];
  if (filter === 'unread') {
    return {
      conversations: conversations.filter(
        (c) => c.status === 'new' || c.status === 'reading',
      ),
    };
  }
  return { conversations };
}

export const Route = createFileRoute('/sudo/inbox/')({
  component: InboxList,
});

function InboxList() {
  useDocumentTitle('Inbox · super-admin');
  const [filter, setFilter] = useState<string>('unread');
  const query = useQuery({
    queryKey: ['sudo', 'conversations', filter],
    queryFn: () => fetchConversations(filter),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inbox</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Contact-form + in-app + direct-email messages.
          </p>
        </div>
        {query.data ? (
          <span className="text-xs text-muted-foreground">
            {query.data.conversations.length} shown
          </span>
        ) : null}
      </header>

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              filter === f.value
                ? 'bg-brand-ink text-brand-cream'
                : 'bg-secondary text-brand-ink-soft hover:bg-secondary/70'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading&hellip;</p>
      ) : query.error || !query.data ? (
        <p className="text-sm text-destructive">Failed to load conversations.</p>
      ) : query.data.conversations.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-brand-sage/30 bg-card/50 p-10 text-center text-sm text-muted-foreground">
          Nothing here.
        </p>
      ) : (
        <ConversationsTable rows={query.data.conversations} />
      )}
    </div>
  );
}

function ConversationsTable({ rows }: { rows: ConversationRow[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-sage/15 bg-card shadow-brand">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-brand-sage/10 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Person</th>
              <th className="px-4 py-3">Channel</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Snippet</th>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-b border-brand-sage/10 last:border-b-0 hover:bg-secondary/40"
              >
                <td className="px-4 py-3">
                  <span className="block font-medium">{r.name || r.email || '—'}</span>
                  {r.name && r.email ? (
                    <span className="block text-xs text-muted-foreground">{r.email}</span>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <ChannelBadge channel={r.channel} />
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={r.status} />
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  <span className="line-clamp-2">{r.painSummary || ''}</span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {formatRelative(r.updatedAt)}
                </td>
                <td className="px-4 py-3">
                  <Link
                    to="/sudo/inbox/$id"
                    params={{ id: r.id }}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-secondary"
                  >
                    Open
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: Status }) {
  const styles: Record<Status, string> = {
    new: 'bg-amber-100 text-amber-900',
    reading: 'bg-blue-100 text-blue-900',
    replied: 'bg-emerald-100 text-emerald-900',
    closed: 'bg-zinc-100 text-zinc-700',
    spam: 'bg-rose-100 text-rose-900',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function ChannelBadge({ channel }: { channel: Channel }) {
  const map: Record<Channel, { label: string; Icon: typeof Mail }> = {
    contact_form: { label: 'web', Icon: Megaphone },
    app_contact_form: { label: 'app', Icon: MessageSquare },
    direct_email: { label: 'email', Icon: Mail },
  };
  const { label, Icon } = map[channel];
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

function formatRelative(epochMs: number): string {
  const diff = Date.now() - epochMs;
  if (diff < 0) return 'in the future';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
